import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Mail, Globe, MapPin } from 'lucide-react';

// ─── Social icons ─────────────────────────────────────────────────────────────
const SocialRow = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 1.25rem;

  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const SocialLink = styled.a`
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: rgba(255,255,255,0.08);
  border: 1px solid rgba(255,255,255,0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s, border-color 0.2s, transform 0.2s;
  color: rgba(255,255,255,0.75);

  &:hover {
    background: ${props => props.$hoverBg || props.theme.colors.orange};
    border-color: transparent;
    color: #ffffff;
    transform: translateY(-2px);
  }

  svg {
    width: 18px;
    height: 18px;
    fill: currentColor;
  }
`;

// ─── Main footer body ──────────────────────────────────────────────────────────
const FooterBody = styled.div`
  background: ${props => props.theme.colors.navy};
  color: #ffffff;
  padding: 4rem 2rem 2rem;
`;

const Grid = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 2fr 1fr 1.2fr;
  gap: 3rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr 1fr;
    gap: 2rem;
    text-align: center;

    /* Hide the Quick Links column (second child) */
    & > *:nth-child(2) {
      display: none;
    }
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const BrandCol = styled.div`
  .logo-img {
    height: 48px;
    width: auto;
    display: block;
    border-radius: 8px;
    margin-bottom: 1rem;
  }

  .tagline {
    color: rgba(255,255,255,0.65);
    font-size: 0.9375rem;
    line-height: 1.6;
    max-width: 280px;
    margin-bottom: 1.25rem;
  }

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    align-items: center;

    .tagline {
      max-width: 100%;
    }
  }
`;

const Col = styled.div`
  h4 {
    font-family: ${props => props.theme.fonts.sans};
    font-weight: 600;
    font-size: 0.9375rem;
    color: #ffffff;
    margin-bottom: 1.25rem;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  ul {
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }
`;

const FootLink = styled(Link)`
  color: rgba(255,255,255,0.65);
  font-size: 0.9375rem;
  transition: color 0.2s;

  &:hover { color: ${props => props.theme.colors.orange}; }
`;

const ContactItem = styled.a`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  color: rgba(255,255,255,0.65);
  font-size: 0.9375rem;
  transition: color 0.2s;
  text-decoration: none;
  line-height: 1.4;

  &:hover { color: ${props => props.theme.colors.orange}; }

  svg { flex-shrink: 0; margin-top: 1px; }

  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const Divider = styled.div`
  max-width: 1280px;
  margin: 3rem auto 0;
  border-top: 1px solid rgba(255,255,255,0.1);
`;

// ─── Bottom copyright bar ──────────────────────────────────────────────────────
const FooterBar = styled.footer`
  background: transparent;
  padding: 1.5rem 2rem;
  text-align: center;
`;

const FooterText = styled.p`
  color: rgba(0,0,0,0.45);
  font-size: 0.8125rem;
  line-height: 1.6;
  margin: 0;
`;

function Footer() {
  return (
    <>
      <FooterBody>
        <Grid>
          <BrandCol>
            <img src="/inbookingslogo.jpg" alt="InBookings" className="logo-img" />
            <p className="tagline">Your premium bookings platform for the perfect convenience.</p>
            <SocialRow>
              {/* Instagram */}
              <SocialLink
                href="https://instagram.com/inbookings"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="InBookings on Instagram"
                $hoverBg="linear-gradient(45deg,#f09433,#e6683c,#dc2743,#cc2366,#bc1888)"
              >
                <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
              </SocialLink>
              {/* WhatsApp */}
              <SocialLink
                href="https://wa.me/2347077219592"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Chat with InBookings on WhatsApp"
                $hoverBg="#25D366"
              >
                <svg viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
                  <path d="M16.003 2.667C8.639 2.667 2.667 8.638 2.667 16c0 2.35.632 4.64 1.833 6.642L2.667 29.333l6.878-1.796A13.27 13.27 0 0016.003 29.333C23.365 29.333 29.333 23.362 29.333 16S23.365 2.667 16.003 2.667zm0 24.133a10.93 10.93 0 01-5.575-1.526l-.4-.237-4.082 1.066 1.087-3.975-.261-.41A10.906 10.906 0 015.11 16c0-6.006 4.888-10.893 10.894-10.893S26.896 9.994 26.896 16 22.009 26.8 16.003 26.8zm5.976-8.163c-.327-.164-1.934-.954-2.234-1.062-.299-.11-.517-.164-.734.163-.218.328-.844 1.063-1.035 1.281-.19.217-.381.245-.708.082-.327-.164-1.38-.509-2.628-1.621-.972-.866-1.628-1.935-1.818-2.262-.19-.327-.02-.504.143-.667.147-.146.327-.381.49-.572.164-.19.218-.327.327-.544.11-.218.055-.408-.027-.572-.082-.163-.734-1.771-1.006-2.424-.265-.637-.534-.55-.735-.56l-.626-.01c-.218 0-.572.081-.871.408-.299.327-1.143 1.117-1.143 2.724s1.17 3.158 1.334 3.376c.163.217 2.302 3.514 5.578 4.928.78.337 1.388.538 1.863.688.783.249 1.496.214 2.059.13.628-.094 1.934-.79 2.207-1.553.272-.762.272-1.416.19-1.553-.08-.136-.298-.217-.625-.38z"/>
                </svg>
              </SocialLink>
            </SocialRow>
          </BrandCol>

          <Col>
            <h4>Quick Links</h4>
            <ul>
              <li><FootLink to="/">Home</FootLink></li>
              <li><FootLink to="/listings">Listings</FootLink></li>
              <li><FootLink to="/about">About</FootLink></li>
              <li><FootLink to="/list-your-space">List Your Space</FootLink></li>
              <li><FootLink to="/admin">Admin</FootLink></li>
            </ul>
          </Col>

          <Col>
            <h4>Contact</h4>
            <ul>
              <li>
                <ContactItem href="mailto:contact@inbookingsonline.com">
                  <Mail size={15} strokeWidth={1.75} />
                  contact@inbookingsonline.com
                </ContactItem>
              </li>
              <li>
                <ContactItem href="https://inbookingsonline.com" target="_blank" rel="noopener noreferrer">
                  <Globe size={15} strokeWidth={1.75} />
                  inbookingsonline.com
                </ContactItem>
              </li>
              <li>
                <ContactItem as="span">
                  <MapPin size={15} strokeWidth={1.75} />
                  Office 7, Sharon Plaza, Kachia Road, Kaduna (By Sharon Filling Station)
                </ContactItem>
              </li>
            </ul>
          </Col>
        </Grid>

        <Divider />
      </FooterBody>

      <FooterBar>
        <FooterText>
          &copy; {new Date().getFullYear()} InBookings. All rights reserved. Registered in Nigeria &mdash; RC 9376359
        </FooterText>
      </FooterBar>
    </>
  );
}

export default Footer;
